// Results page headline: a valid visibility baseline is not an outcome.
// Read ONLY from the backend measurement contract (decisions.py
// `_ai_measurement`). A check row in history is not enough -- failed /
// UNKNOWN measurements must not be presented as a successful baseline.
// `intervention` stays the backend's approved-work flag; this helper
// never invents one.

const OUTCOME_RESULTS = new Set(['improving', 'no_change', 'worse', 'not_comparable']);

export function hasValidBaseline(m) {
  if (!m?.supported || !m.baseline) return false;
  const of = Number(m.baseline.of);
  return Number.isFinite(of) && of > 0;
}

export function resultsPageCopy(measured, monitoring) {
  const rows = (measured || []).filter((d) => d?.measurement?.supported);
  const withFollowUp = rows.filter((d) => d.measurement?.intervention && d.measurement?.latest);
  const awaiting = rows.filter((d) => d.measurement?.intervention && !d.measurement?.latest);
  const baselineOnly = rows.filter((d) => hasValidBaseline(d.measurement) && !d.measurement?.intervention);
  const hasOutcome = withFollowUp.length > 0 || rows.some((d) => OUTCOME_RESULTS.has(d.measurement?.result));
  const mixedKinds = [hasOutcome, awaiting.length > 0, baselineOnly.length > 0].filter(Boolean).length;

  // A workspace can hold decisions in different states. The page headline
  // must not treat one outcome as if every decision had reached it.
  if (mixedKinds > 1) {
    return {
      title: 'Each decision is measured on its own.',
      summary: 'A result appears only after approved work and a later comparable check. A visibility baseline without approved work is not an outcome. Read each decision for its own state.',
    };
  }

  if (hasOutcome) {
    return {
      title: 'Did it work?',
      summary: monitoring?.recurring
        ? 'Martian compares each check after you approved the work with the baseline before it.'
        : 'You approved work, but nothing is measured until you approve the questions Martian watches. That switches on the weekly checks.',
    };
  }
  if (awaiting.length) {
    return {
      title: 'Waiting for a follow-up measurement.',
      summary: monitoring?.recurring
        ? 'Work is approved. A business outcome appears after a later, comparable measurement against this baseline.'
        : 'You approved work, but nothing is measured until you approve the questions Martian watches. That switches on the weekly checks.',
    };
  }
  if (baselineOnly.length) {
    return {
      title: 'Visibility baseline recorded.',
      summary: 'Martian recorded how often this brand appeared in the usable answers from the watched questions. That figure is a visibility baseline for this prompt set, not a claim about every AI system. No work has been approved yet. A business outcome requires an approved intervention, then a later comparable measurement.',
    };
  }
  return {
    title: 'Nothing is being measured yet',
    summary: 'Martian measures a result after two approvals: the work it prepared, and the questions it watches. It records the baseline now, so it can show you what changed.',
  };
}
